import React, {
    useCallback, useContext, useEffect, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { TranslatorContext } from '@jutro/locale';
import {
    Button,
    ModalNext,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Link
} from '@jutro/components';
import { SgDate } from 'sg-date';
import _ from 'lodash';
import SgProductService from 'sg-product-service';
import SgSessionStorageService from 'sg-session-storage';
import { useValidation } from 'gw-portals-validation-react';
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { SgDateUtil } from 'saga-modules';
import CommonConstants from 'sg-common-constants';
import messages from './SgExpiredQuoteModal.messages';

function SgExpiredQuoteModal(props) {
    const {
        modelVM,
        isOpen,
        onResolve,
        additionalLogicOnContinue
    } = props;
    const {
        onValidate,
        isComponentValid,
    } = useValidation('periodStartDate');

    const translator = useContext(TranslatorContext);
    const [model, updateModel] = useState(_.clone(modelVM));
    const [showErrors, setShowErrors] = useState(false);
    const [showWizardLoader, setShowWizardLoader] = useState(false);

    useEffect(() => {
        SgSessionStorageService.removeItem(SgSessionStorageService.KEY.IS_RELOADING);
        SgSessionStorageService.removeItem(SgSessionStorageService.KEY.PREVENT_SKIP_TO_QUOTE_IN_RETRIEVAL);
        modelVM.baseData.periodStartDate.value = {};
        updateModel(_.clone(modelVM));
    }, []);

    const updateDate = useCallback((newModel) => {
        if (newModel.quoteData && newModel.quoteData.offeredQuotes && newModel.quoteData.offeredQuotes.length > 0
            && newModel.baseData.periodStartDate.aspects.valid) {
            newModel.quoteData.offeredQuotes.forEach((opt) => {
                // eslint-disable-next-line no-param-reassign
                opt.periodStartDate = SgDateUtil.toMomentDate(newModel.baseData.periodStartDate.value).utc(true).toISOString();
            });
        }
        updateModel(_.clone(newModel));
    }, []);

    const maxDateParam = SgSessionStorageService.getItem(SgSessionStorageService.KEY.MAX_START_DATE);
    const {
        minDate,
        maxDate
    } = useMemo(() => {
        if (SgProductService.isSmi()) {
            return {
                minDate: moment().add(1, 'days').toDate(),
                maxDate: maxDateParam
            };
        }
        return {
            minDate: new Date(),
            maxDate: moment().add(1, 'years').toDate()
        };
    }, [maxDateParam]);

    const onContinue = useCallback((isReviewingDetails, event) => {
        if (event) {
            event.preventDefault();
        }
        if (!isReviewingDetails && !isComponentValid) {
            setShowErrors(true);
            return;
        }
        if (!isReviewingDetails && additionalLogicOnContinue) {
            additionalLogicOnContinue(model);
        }
        model.value.hasUpdatedPeriodStartDate = !isReviewingDetails;
        model.value.isExpiredQuote = false;
        if (!isReviewingDetails) {
            setShowWizardLoader(true);
            SgSessionStorageService.setItem(SgSessionStorageService.KEY.IS_RETRIEVAL, true);
            LoadSaveService.saveAndQuoteSubmission(model)
                .then((updatedSubmission) => {
                    onResolve(updatedSubmission);
                })
                .catch((error) => {
                    onResolve('error');
                    if (_.isNil(error.appErrorCode)) {
                        _.set(error, 'appErrorCode', CommonConstants.SG_ERROR_CODES.SAGA_WEBSITE_ERROR); // Website error
                    }
                    throw error;
                });
        } else {
            SgSessionStorageService.setItem(SgSessionStorageService.KEY.PREVENT_SKIP_TO_QUOTE_IN_RETRIEVAL, isReviewingDetails);
            onResolve(model);
        }
    }, [isComponentValid, model, onResolve, additionalLogicOnContinue]);

    const secondaryLabel = useMemo(() => {
        return (
            <>
                {translator(messages.DateHelp)}
                { !model.aggregatorDetailsDTO_Ext.isAggregatorQuote_Ext.value
                && (
                    <p>
                        {translator(messages.DetailsChanged)}
                        <Link href="/" id="reviewDetailsLink" onClick={(event) => onContinue(true, event)}>
                            {translator(messages.ReviewDetails)}
                        </Link>
                    </p>
                )}
            </>
        );
    }, []);

    return (
        <ModalNext isOpen={isOpen}>
            <div className="sgExpiredQuoteModal sgInfoModal">
                <ModalHeader title={translator(messages.ExpireQuoteHeader)} />
                <ModalBody autoFocus={false}>
                    <SgDate
                        id="periodStartDate"
                        modelVM={model}
                        showErrors={showErrors}
                        updateData={updateDate}
                        minDate={minDate}
                        maxDate={maxDate}
                        path="baseData.periodStartDate"
                        showValidationIcon={false}
                        label={translator(messages.DateLabel)}
                        secondaryLabel={secondaryLabel}
                        onValidate={onValidate}
                    />
                </ModalBody>
                <ModalFooter>
                    { showWizardLoader
                        ? <div className="sgAnimatedLoader" />
                        : (
                            <Button className="sgButton sgTransactionButton" onClick={() => onContinue(false)}>
                                {translator(messages.Continue)}
                            </Button>
                        )
                    }
                </ModalFooter>
            </div>
        </ModalNext>

    );
}

SgExpiredQuoteModal.propTypes = {
    isOpen: PropTypes.bool,
};

SgExpiredQuoteModal.defaultProps = {
    isOpen: true,
};

export default SgExpiredQuoteModal;
